import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { formatMediumDate } from "@/lib/dates";
import { SORT_OPTIONS } from "@/lib/tutorSort";

// "How we order tutors" (#1096): how tutors are listed, ordered and removed, what the badges mean and that no
// place can be bought. French law (Code de la consommation D111-6) and the EU rules on ranking want it
// reachable from every page and from the results. It says only what is live: **any PR that changes an order, who
// is listed or a badge changes this page in the same PR** (CLAUDE.md). Every sentence is a locale key, so a
// student reads it in their own language, unlike the Terms (#684). The server gives it its title and its meta
// tags (MarketingController), the body is meta-only like /terms.
const UPDATED = "2026-10-26";

const link = "underline";

export default function HowWeOrderTutors() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-24 max-w-3xl">
        <Button variant="ghost" size="sm" className="mb-6 -ml-2" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-1" />
          {t("howWeOrderTutors.back")}
        </Button>
        <h1 className="text-3xl font-display font-bold text-foreground mb-6">{t("howWeOrderTutors.title")}</h1>
        <div className="prose text-muted-foreground space-y-4 text-sm">
          <p>{t("howWeOrderTutors.lastUpdated", { date: formatMediumDate(UPDATED, i18n.language) })}</p>
          <p>{t("howWeOrderTutors.intro")}</p>

          <h2 className="text-lg font-semibold text-foreground mt-6">{t("howWeOrderTutors.who.title")}</h2>
          <p>{t("howWeOrderTutors.who.body")}</p>

          <h2 className="text-lg font-semibold text-foreground mt-6">{t("howWeOrderTutors.orders.title")}</h2>
          <p>{t("howWeOrderTutors.orders.intro")}</p>
          <ul className="list-disc pl-6 space-y-1" data-testid="sort-definitions">
            {SORT_OPTIONS.map((option) => (
              <li key={option.value}>
                <strong className="text-foreground">{t(option.labelKey)}.</strong> {t(option.helpKey)}
              </li>
            ))}
          </ul>
          <p>{t("howWeOrderTutors.orders.filters")}</p>
          <p>{t("howWeOrderTutors.orders.similar")}</p>
          <p>{t("howWeOrderTutors.orders.learnPages")}</p>
          <p>{t("howWeOrderTutors.orders.courses")}</p>
          <p>{t("howWeOrderTutors.orders.why")}</p>

          <h2 className="text-lg font-semibold text-foreground mt-6">{t("howWeOrderTutors.removed.title")}</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <Trans
                i18nKey="howWeOrderTutors.removed.breach"
                components={{
                  terms: <Link to="/terms#how-we-moderate" className={link} />,
                  guidelines: <Link to="/community-guidelines" className={link} />,
                }}
              />
            </li>
            <li>{t("howWeOrderTutors.removed.hide")}</li>
            <li>{t("howWeOrderTutors.removed.choice")}</li>
            <li>{t("howWeOrderTutors.removed.inactive")}</li>
          </ul>

          <h2 className="text-lg font-semibold text-foreground mt-6">{t("howWeOrderTutors.bought.title")}</h2>
          <p>{t("howWeOrderTutors.bought.body")}</p>

          <h2 className="text-lg font-semibold text-foreground mt-6">{t("howWeOrderTutors.badges.title")}</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>{t("howWeOrderTutors.badges.native")}</li>
            <li>{t("howWeOrderTutors.badges.new")}</li>
            <li>{t("howWeOrderTutors.badges.verified")}</li>
            <li>{t("howWeOrderTutors.badges.away")}</li>
            <li>{t("howWeOrderTutors.badges.rating")}</li>
            <li>{t("howWeOrderTutors.badges.numbers")}</li>
          </ul>

          <h2 className="text-lg font-semibold text-foreground mt-6">{t("howWeOrderTutors.changes.title")}</h2>
          <p>
            <Trans i18nKey="howWeOrderTutors.changes.body" components={{ terms: <Link to="/terms#how-we-moderate" className={link} /> }} />
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
